import React from "react";
import PageTitle from "@Components/PageTitle/PageTitle";
import themeCreator from "@Themes/themeCreator";
import {
  Container,
  Typography,
  Box,
  List,
  ListItemText,
  ListItemIcon,
  Grid,
  Paper,
  CircularProgress,
  ListItemButton,
  IconButton,
  Divider,
  Chip,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Button,
} from "@mui/material";

import PDFViewer from "@Components/PDFViewer/PDFViewer";
import DescriptionIcon from "@mui/icons-material/Description";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PaymentIcon from "@mui/icons-material/Payment";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import NumbersIcon from "@mui/icons-material/Numbers";
import BusinessIcon from "@mui/icons-material/Business";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { format } from "date-fns";

import { ListingPageContainer } from "../ListingPageTemplate/ListingPageTemplateStyles";
import { useEffect, useState } from "react";
import useGetAll from "@Hooks/useGetAll";
import {
  GET_INVOICE_BY_ID,
  UPDATE_INVOICES_URL,
  UPDATE_PAYMENTS_URL,
  ADD_PAYMENT_URL,
} from "@API/APIRoutes";
import { EditSectionPopup } from "../GenericForm/EditSectionPopup";

import useDeleteItem from "@Hooks/useDeleteItem";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { AddPaymentPopup } from "../GenericFullOrderInfo/AddPaymentPopup";

import useSendPutRequest from "@Hooks/useSendPutRequest";
import usePostItem from "@Hooks/usePostItem";
import {
  inputTypes,
  Currency,
  paymentTypes,
  TransactionTypes,
  InvoiceStates,
} from "../../Utils/HelpingObjects/HelpingObjects";
import axios from "axios";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import PaidIcon from "@mui/icons-material/Paid";
import CommentIcon from "@mui/icons-material/Comment";
import { decryptId, encryptId } from "../../Utils/SecurityFunctions";
import * as DefaultRoutes from "@Routes/DefaultRoutes";

export default function PaymentPreview() {
  const theme = themeCreator();
  const invoiceId = decryptId(useParams().invoiceId);
  const { loading, getAll } = useGetAll();
  const [invoice, setInvoice] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const navigate = useNavigate();

  // Format date helper function
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "MMM dd, yyyy");
    } catch (error) {
      return "Invalid Date";
    }
  };
  /******************* Edit Invoice *******************/
  const [showInvoiceEditPopup, setShowInvoiceEditPopup] = useState(false);
  const { requestMethod: invoicePutItem } = useSendPutRequest();
  const [invoicePutItemLoading, setInvoicePutItemLoading] = useState(false);
  const { loading: insertPaymentLoading, postItem: insertPayment } =
    usePostItem();
  const invoiceFormFields = [
    {
      size: 4,
      type: inputTypes.TEXT,
      key: "transactionNumber",
      label: "Transaction Number",
      editable: false,
    },
    {
      size: 4,
      type: inputTypes.TEXT,
      key: "poNumber",
      label: "Po Number",
      editable: false,
    },
    {
      size: 4,
      type: inputTypes.TEXT,
      key: "coraId",
      label: "Cora Id",
      editable: true,
    },
    {
      size: 4,
      type: inputTypes.SEARCHABLE_DROPDOWN,
      key: "state",
      label: "State",
      options: InvoiceStates,
      editable: true,
    },
    {
      size: 4,
      type: inputTypes.SEARCHABLE_DROPDOWN,
      key: "transactionType",
      label: "Transaction Type",
      options: TransactionTypes,
      editable: true,
    },
    {
      size: 4,
      type: inputTypes.FLOAT_NUMBER,
      key: "totalInvoiceCost",
      label: "Total Cost",
      editable: true,
    },
    {
      size: 12,
      type: inputTypes.SEARCHABLE_DROPDOWN,
      key: "currency",
      label: "Currency",
      options: Currency,
      editable: true,
    },

    {
      size: 4,
      type: inputTypes.DATE,
      key: "releasedDate",
      label: "Released Date",
      editable: true,
    },
    {
      size: 4,
      type: inputTypes.DATE,
      key: "submissionDate",
      label: "Submission Date",
      editable: true,
    },
    {
      size: 4,
      type: inputTypes.DATE,
      key: "dueDate",
      label: "Due Date",
      editable: true,
    },

    {
      size: 12,
      type: inputTypes.TEXT,
      key: "comment",
      label: "Comment",
      editable: true,
    },
  ];
  const formatInvoiceDate = (date) => {
    if (!date) return null;
    return typeof date.toDate === "function" ? date.toDate() : date;
  };

  const onEditInvoice = (invoice) => {
    setShowInvoiceEditPopup(true);
  };
  const closeInvoiceEditPopup = () => setShowInvoiceEditPopup(false);

  const confirmInvoiceEdit = async (newInvoiceData, file) => {
    setInvoicePutItemLoading(true);

    // Create a copy of the data and remove the payments array
    const { payments, ...dataWithoutPayments } = newInvoiceData;

    const processedDataWithoutPayments = {
      ...dataWithoutPayments,
      releasedDate: formatInvoiceDate(dataWithoutPayments.releasedDate),
      submissionDate: formatInvoiceDate(dataWithoutPayments.submissionDate),
      dueDate: formatInvoiceDate(dataWithoutPayments.dueDate),
      isFileAttached: file ? true : false,
      isReal: true,
    };

    const response = await invoicePutItem(
      UPDATE_INVOICES_URL,
      processedDataWithoutPayments,
      `Invoice Edited Successfully!`,
      `Failed To Edit Invoice`
    );

    // Extract preSignedUrl and objectKey outside the .then()
    if (file && response?.data?.preSignedUrl) {
      const { preSignedUrl, objectKey } = response.data;
      // Upload file to Minio
      await axios.put(preSignedUrl, file, {
        headers: {
          "Content-Type": "application/pdf",
        },
      });
    }

    // Update the local invoice state with the edited attributes while preserving payments
    setInvoice((prevInvoice) => ({
      ...prevInvoice,
      ...processedDataWithoutPayments,
      // Keep the original payments array
      payments: prevInvoice.payments,
    }));
    //refresh the page to show the updated invoice
    window.location.href = window.location.href;
    closeInvoiceEditPopup();
  };
  //**************************** Payment Edit ****************************/
  const [showPaymentEditPopup, setShowPaymentEditPopup] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const { isLoading: paymentPutItemLoading, requestMethod: paymentPutItem } =
    useSendPutRequest();
  const paymentFormFields = [
    {
      size: 12,
      type: inputTypes.TEXT,
      key: "paymentName",
      label: "Payment Name",
      editable: true,
    },
    {
      size: 12,
      type: inputTypes.SEARCHABLE_DROPDOWN,
      key: "paymentType",
      label: "Payment Type",
      options: paymentTypes,
      editable: true,
    },
    {
      size: 12,
      type: inputTypes.FLOAT_NUMBER,
      key: "amount",
      label: "Amount",
      editable: true,
    },
    {
      size: 12,
      type: inputTypes.SEARCHABLE_DROPDOWN,
      key: "currency",
      label: "Currency",
      options: Currency,
      editable: true,
    },
    {
      size: 12,
      type: inputTypes.DATE,
      key: "date",
      label: "Payment Date",
      editable: true,
    },
  ];
  const onEditPayment = (payment) => {
    setSelectedPayment(payment);
    setShowPaymentEditPopup(true);
  };
  const closePaymentEditPopup = () => setShowPaymentEditPopup(false);

  const confirmPaymentEdit = async (newPaymentData) => {
    const processedData = {
      ...newPaymentData,
      date: formatInvoiceDate(newPaymentData.date),
    };
    await paymentPutItem(
      UPDATE_PAYMENTS_URL,
      processedData,
      `Payment Edited Successfully!`,
      `Failed To Edit Payment`
    );
    // Update the invoice state with the edited payment
    const updatedPayments = invoice.payments.map((payment) =>
      payment.paymentId === newPaymentData.paymentId ? processedData : payment
    );
    const updatedInvoice = {
      ...invoice,
      payments: updatedPayments,
    };
    setInvoice(updatedInvoice);
    window.location.reload();
    closePaymentEditPopup();
  };
  //**************************** Add Payment ****************************/
  const [showAddPaymentPopup, setShowAddPaymentPopup] = useState(false);
  const closeAddPaymentPopup = () => setShowAddPaymentPopup(false);
  const confirmAddPayment = async (newPaymentData) => {
    const processedData = {
      ...newPaymentData,
      date: formatInvoiceDate(newPaymentData.date),
      paymentId: 10,
    };
    const response = await insertPayment(
      ADD_PAYMENT_URL,
      processedData,
      closeAddPaymentPopup,
      `Payment Added Successfully!`,
      `Failed To Add Payment`
    );
    const returnedResponse = response;
    if (!returnedResponse) return;
    // Update the invoice state with the new payment
    const updatedInvoice = {
      ...invoice,
      payments: [...invoice.payments, returnedResponse],
    };
    setInvoice(updatedInvoice);
    window.location.reload();
    closeAddPaymentPopup();
  };
  const addNewPayment = () => {
    setShowAddPaymentPopup(true);
  };
  useEffect(() => {
    getAll(`${GET_INVOICE_BY_ID}/${invoiceId}`).then((response) => {
      setInvoice(response);

      // If there's a document URL in the response, set it for the PDF viewer
      if (response?.presignedUrl) {
        setPdfUrl(response.presignedUrl);
      }
    });
  }, [invoiceId]);

  const goToOrder = () => {
    if (!invoice?.orderId) return;
    navigate(
      DefaultRoutes.orderInfo.replace(":orderId", encryptId(invoice.orderId))
    );
  };
  return (
    <ListingPageContainer className="listingPageContainer">
      <Container
        sx={{
          pb: 0,
          pr: "0px !important",
          pl: "0px !important",
          overflow: "hidden !important",
        }}
        disableGutters
      >
        <Box className="LabelButtonBox">
          <PageTitle
            title="Payment Preview"
            PageIcon={<ReceiptIcon color="white" fontSize="large" />}
            iconColor={theme.palette.primary.main}
          />
          {invoice?.orderId && (
            <Button
              variant="contained"
              color="primary"
              onClick={goToOrder}
              sx={{ ml: 2, textTransform: "none" }}
            >
              Go To Order
            </Button>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : !invoice ? (
          <Typography variant="body1" sx={{ textAlign: "center", mt: 4 }}>
            No invoice data found.
          </Typography>
        ) : (
          <Grid container spacing={3} sx={{ mt: 2 }}>
            {/* Left Side - Invoice Details (7/12 of width) */}
            <Grid item xs={12} md={7}>
              {/* Invoice Details Card */}
              <Grid item xs={12}>
                <Paper
                  sx={{
                    p: 0,
                    borderRadius: 2,
                    height: "100%",
                    overflow: "hidden",
                    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      transform: "scale(0.95)", // You can later make this dynamic
                      width: "100%",
                      minHeight: "40vh",
                      p: 2,
                      boxSizing: "border-box",
                      overflow: "hidden",
                    }}
                  >
                    {/* Header */}
                    <Box
                      sx={{
                        mb: 1,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderBottom: `2px solid ${theme.palette.primary.light}`,
                        pb: 1,
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.primary.main,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <ReceiptIcon /> Transaction Information
                      </Typography>
                      <IconButton
                        size="small"
                        color="primary"
                        aria-label="edit transaction"
                        onClick={onEditInvoice}
                        sx={{
                          backgroundColor: theme.palette.primary.light,
                          borderRadius: 2,
                          "&:hover": {
                            backgroundColor: theme.palette.primary.main,
                            color: "#fff",
                            transform: "scale(1.05)",
                            boxShadow: "0 3px 8px rgba(0, 0, 0, 0.15)",
                            transition: "all 0.2s ease-in-out",
                          },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* Transaction Details */}
                    <Typography
                      variant="subtitle1"
                      sx={{
                        mt: 1.5,
                        mb: 1,
                        fontWeight: 500,
                        color: theme.palette.text.primary,
                        borderLeft: `3px solid ${theme.palette.primary.main}`,
                        pl: 1,
                      }}
                    >
                      Transaction Details
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={4}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: theme.palette.background.default,
                            pl: 2,
                            borderRadius: 1,
                          }}
                        >
                          <NumbersIcon
                            sx={{ color: theme.palette.primary.main, mr: 1 }}
                          />
                          <Box>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                            >
                              Transaction Number
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {invoice.transactionNumber || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: theme.palette.background.default,
                            p: 0,
                            borderRadius: 1,
                          }}
                        >
                          <BusinessIcon
                            sx={{ color: theme.palette.primary.main, mr: 1 }}
                          />
                          <Box>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                            >
                              Transaction Type
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {invoice.transactionType || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: theme.palette.background.default,
                            p: 0,
                            borderRadius: 1,
                          }}
                        >
                          <BusinessIcon
                            sx={{ color: theme.palette.primary.main, mr: 1 }}
                          />
                          <Box>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                            >
                              State
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {invoice.state}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: theme.palette.background.default,
                            p: 0,
                            pl: 2,
                            borderRadius: 1,
                          }}
                        >
                          <PointOfSaleIcon
                            sx={{ color: theme.palette.primary.main, mr: 1 }}
                          />
                          <Box>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                            >
                              Total Cost
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {invoice.totalInvoiceCost}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: theme.palette.background.default,
                            p: 0,
                            borderRadius: 1,
                          }}
                        >
                          <PaidIcon
                            sx={{ color: theme.palette.primary.main, mr: 1 }}
                          />
                          <Box>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                            >
                              Currency
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {invoice.currency}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: theme.palette.background.default,
                            p: 0,
                            borderRadius: 1,
                          }}
                        >
                          <CommentIcon
                            sx={{ color: theme.palette.primary.main, mr: 1 }}
                          />
                          <Box>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                            >
                              Comment
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {invoice.comment || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Reference Information */}
                    <Typography
                      variant="subtitle1"
                      sx={{
                        mt: 1,
                        mb: 1,
                        fontWeight: 500,
                        color: theme.palette.text.primary,
                        borderLeft: `3px solid ${theme.palette.primary.main}`,
                        pl: 1,
                      }}
                    >
                      Reference Information
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: theme.palette.background.default,
                            pl: 2,
                            borderRadius: 1,
                          }}
                        >
                          <NumbersIcon
                            sx={{ color: theme.palette.primary.main, mr: 1 }}
                          />
                          <Box>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                            >
                              PO Number
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {invoice.poNumber || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: theme.palette.background.default,
                            p: 0,
                            borderRadius: 1,
                          }}
                        >
                          <NumbersIcon
                            sx={{ color: theme.palette.primary.main, mr: 1 }}
                          />
                          <Box>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                            >
                              CORA ID
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {invoice.coraId || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Transaction Dates */}
                    <Typography
                      variant="subtitle1"
                      sx={{
                        mt: 1,
                        mb: 1,
                        fontWeight: 500,
                        color: theme.palette.text.primary,
                        borderLeft: `3px solid ${theme.palette.primary.main}`,
                        pl: 1,
                      }}
                    >
                      Transaction Dates
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: theme.palette.background.default,
                            pl: 2,
                            borderRadius: 1,
                          }}
                        >
                          <CalendarTodayIcon
                            sx={{ color: theme.palette.primary.main, mr: 1 }}
                          />
                          <Box>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                            >
                              Submission Date
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {formatDate(invoice.submissionDate)}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: theme.palette.background.default,
                            p: 0,
                            borderRadius: 1,
                          }}
                        >
                          <CalendarTodayIcon
                            sx={{ color: theme.palette.primary.main, mr: 1 }}
                          />
                          <Box>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                            >
                              Released Date
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {formatDate(invoice.releasedDate)}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: theme.palette.background.default,
                            p: 0,
                            borderRadius: 1,
                          }}
                        >
                          <CalendarTodayIcon
                            sx={{ color: theme.palette.primary.main, mr: 1 }}
                          />
                          <Box>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                            >
                              Due Date
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {formatDate(invoice.dueDate)}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
              </Grid>

              {/* Payments Section */}
              <Grid item xs={12}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                    mt: 1,
                    height: "32vh",
                    overflow: "auto",
                  }}
                >
                  <Box
                    sx={{
                      mb: 3,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      borderBottom: `2px solid ${theme.palette.primary.light}`,
                      pb: 1,
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <PaymentIcon /> Payment Details
                    </Typography>
                    <IconButton
                      size="small"
                      color="success"
                      aria-label="add new payment"
                      onClick={addNewPayment}
                      sx={{
                        backgroundColor: theme.palette.success.light,
                        borderRadius: 2,
                        "&:hover": {
                          backgroundColor: theme.palette.success.main,
                          color: "#fff",
                          transform: "scale(1.05)",
                          boxShadow: "0 3px 8px rgba(0, 0, 0, 0.15)",
                          transition: "all 0.2s ease-in-out",
                        },
                      }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  {invoice.payments && invoice.payments.length > 0 ? (
                    <Grid container spacing={2}>
                      {invoice.payments.map((payment, index) => (
                        <Grid item xs={12} key={index}>
                          <Card
                            elevation={2}
                            sx={{
                              mb: 2,
                              borderLeft: `4px solid ${theme.palette.primary.main}`,
                              transition: "transform 0.2s ease",
                              "&:hover": {
                                transform: "translateY(-3px)",
                                boxShadow: "0 8px 15px rgba(0,0,0,0.1)",
                              },
                            }}
                          >
                            <CardHeader
                              avatar={
                                <Avatar
                                  sx={{ bgcolor: theme.palette.primary.main }}
                                >
                                  <AttachMoneyIcon />
                                </Avatar>
                              }
                              title={payment.paymentName}
                              subheader={`${payment.paymentType || "N/A"}`}
                              action={
                                <Box sx={{ display: "flex", gap: 0.5 }}>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    aria-label="edit payment"
                                    onClick={() => onEditPayment(payment)}
                                    sx={{
                                      backgroundColor:
                                        "rgba(25, 118, 210, 0.1)",
                                      "&:hover": {
                                        backgroundColor:
                                          theme.palette.primary.main,
                                        color: "#fff",
                                        transform: "scale(1.05)",
                                        boxShadow: "0 2px 5px rgba(0,0,0,0.15)",
                                      },
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              }
                            />
                            <CardContent>
                              <Grid container spacing={2}>
                                <Grid item xs={6}>
                                  <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                  >
                                    Amount
                                  </Typography>
                                  <Typography
                                    variant="body1"
                                    fontWeight="medium"
                                  >
                                    {payment.amount?.toLocaleString()}{" "}
                                    {payment.currency || ""}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                  >
                                    Payment Date
                                  </Typography>
                                  <Typography variant="body1">
                                    {formatDate(payment.date)}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Box
                      sx={{
                        p: 3,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "#f5f5f5",
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="body1" color="text.secondary">
                        No payment details available
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>

            {/* Right Side - PDF Viewer (5/12 of width) */}
            <Grid item xs={12} md={5}>
              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  height: "78vh",
                  borderRadius: 2,
                  overflow: "hidden",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                    pb: 1,
                    minHeight: "40px",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: theme.palette.primary.main }}
                  >
                    Transaction Document
                  </Typography>
                  <Chip
                    icon={<DescriptionIcon />}
                    label={invoice.isFileAttached ? "File Attached" : "No File"}
                    color={invoice.isFileAttached ? "success" : "default"}
                    variant="outlined"
                  />
                </Box>

                {pdfUrl ? (
                  <Box
                    sx={{
                      flexGrow: 1,
                      height: "calc(100% - 50px)",
                      display: "flex",
                      overflow: "hidden",
                    }}
                  >
                    <PDFViewer
                      fileUrl={pdfUrl}
                      style={{
                        height: "100%",
                        width: "100%",
                        border: "none",
                        flexGrow: 1,
                      }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flexDirection: "column",
                      backgroundColor: "#f5f5f5",
                      borderRadius: 1,
                    }}
                  >
                    <DescriptionIcon
                      sx={{ fontSize: 60, color: "#bdbdbd", mb: 2 }}
                    />
                    <Typography variant="body1" color="text.secondary">
                      No document attached to this invoice
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}
      </Container>
      {EditSectionPopup(
        "Invoice",
        showInvoiceEditPopup,
        closeInvoiceEditPopup,
        confirmInvoiceEdit,
        invoicePutItemLoading,
        invoiceFormFields,
        invoice,
        true
      )}
      {EditSectionPopup(
        "Payment",
        showPaymentEditPopup,
        closePaymentEditPopup,
        confirmPaymentEdit,
        paymentPutItemLoading,
        paymentFormFields,
        selectedPayment
      )}
      {AddPaymentPopup(
        invoiceId,
        showAddPaymentPopup,
        closeAddPaymentPopup,
        confirmAddPayment,
        insertPaymentLoading
      )}
    </ListingPageContainer>
  );
}
